import {
  UserGroupIcon,
  ClockIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import {
  v2TeamActivity,
  v2TeamMembers,
  v2TeamSharedWatchlists,
} from "../services/tradingAgentsV2";

export default function Team() {
  const activityQuery = useQuery({
    queryKey: ["team-activity"],
    queryFn: () => v2TeamActivity({ limit: 30 }),
    refetchInterval: 15000,
  });

  const membersQuery = useQuery({
    queryKey: ["team-members"],
    queryFn: v2TeamMembers,
  });

  const watchlistsQuery = useQuery({
    queryKey: ["team-shared-watchlists"],
    queryFn: v2TeamSharedWatchlists,
  });

  const activities = activityQuery.data?.activities ?? [];
  const members = membersQuery.data?.members ?? [];
  const watchlists = watchlistsQuery.data?.watchlists ?? [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Members */}
        <div className="card">
          <div className="mb-4 flex items-center gap-2">
            <UserGroupIcon className="h-5 w-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
          </div>
          {members.length === 0 ? (
            <p className="text-sm text-gray-500">No team members found.</p>
          ) : (
            <ul className="space-y-3">
              {members.map((m) => (
                <li key={m.id} className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                    {(m.full_name || m.email)
                      .split(" ")
                      .map((w) => w[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {m.full_name || m.email}
                    </p>
                    <p className="truncate text-xs text-gray-500">{m.email}</p>
                  </div>
                  {m.role && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {m.role}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Shared watchlists */}
        <div className="card">
          <div className="mb-4 flex items-center gap-2">
            <EyeIcon className="h-5 w-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              Shared Watchlists
            </h2>
          </div>
          {watchlists.length === 0 ? (
            <p className="text-sm text-gray-500">
              No shared watchlists in your organization.
            </p>
          ) : (
            <ul className="space-y-2">
              {watchlists.map((wl) => (
                <li
                  key={wl.id}
                  className="rounded-lg border border-gray-200 p-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{wl.name}</span>
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {(wl as unknown as { item_count?: number }).item_count ?? 0} tickers
                    </span>
                  </div>
                  <div className="mt-1 flex gap-2 text-xs text-gray-500">
                    <span>{wl.schedule || "manual"}</span>
                    {wl.last_run_at && (
                      <span>
                        Last: {new Date(wl.last_run_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Activity feed */}
        <div className="card lg:col-span-1">
          <div className="mb-4 flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              Team Activity
            </h2>
          </div>
          {activities.length === 0 ? (
            <p className="text-sm text-gray-500">No recent activity.</p>
          ) : (
            <ul className="space-y-3">
              {activities.map((a) => (
                <li
                  key={a.run_id}
                  className="border-l-2 border-gray-200 pl-3"
                >
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-900">
                      {a.user_name || "Unknown"}
                    </span>
                    <span className="text-gray-500">analyzed</span>
                    <span className="font-semibold text-primary-700">
                      {a.ticker}
                    </span>
                  </div>
                  <div className="mt-0.5 flex gap-2 text-xs text-gray-400">
                    <span
                      className={`rounded px-1 ${
                        a.status === "completed"
                          ? "bg-green-100 text-green-700"
                          : a.status === "running"
                            ? "bg-blue-100 text-blue-700"
                            : a.status === "failed"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {a.status}
                    </span>
                    {a.llm_provider && <span>{a.llm_provider}</span>}
                    {a.created_at && (
                      <span>{new Date(a.created_at).toLocaleString()}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
